import { Grid, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EventTable from '../../components/client/ClientEventTable'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import ClientBasicInformation from '../../components/client/ClientBasicInformation'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { getEmptyClientObject } from '../../utils/client-utils'
import { ClientDto } from '../../services/api/sharedTypes/Client'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { ClientManager } from '../../services/api/ClientManager'
import { RouteConstants } from '../../navigation/navigation-types'
import { setSaveState } from '../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../components/common/CustomContainer'
import EmailSwitches from '../../components/client/EmailSwitches'
import BottomBar from '../../components/common/BottomBar'

const ClientDetail: React.FC = () => {
    const { id } = useParams()
    const theme = useTheme()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [loadingData, setLoadingData] = useState<boolean>(true)

    const [client, setClient] = useState<ClientDto>(getEmptyClientObject())
    const [oldClient, setOldClient] = useState<ClientDto>(getEmptyClientObject())

    const canGoBack =
        client.name === oldClient.name &&
            client.address.city === oldClient.address.city &&
            client.address.street === oldClient.address.street &&
            client.address.zip === oldClient.address.zip &&
            client.valueAddedTaxIdentificationNumber === oldClient.valueAddedTaxIdentificationNumber &&
            client.companyRegistrationNumber === oldClient.companyRegistrationNumber &&
            client.clientType === oldClient.clientType &&
            client.note === oldClient.note &&
            client.notifications.length === oldClient.notifications.length &&
            client.contacts.length === oldClient.contacts.length &&
            client.notifications.every(
                (notification, index) =>
                    notification.note === oldClient.notifications[index].note &&
                    notification.date === oldClient.notifications[index].date
            ) &&
            client.contacts.every(
                (contact, index) =>
                    contact.email === oldClient.contacts[index].email &&
                    contact.name === oldClient.contacts[index].name &&
                    contact.phone === oldClient.contacts[index].phone
            )
            ? true
            : false

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    if (!id)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tento klient neexistuje
            </Typography>
        )

    React.useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)
            try {
                const clientRes = await ClientManager.getManager().getClientAsync(id)
                if (!clientRes) throw new Error('Klient nenalezen')
                setClient(clientRes)
                setOldClient(clientRes)
            } catch (error) {
                setLoadingData(false)
                dispatch(displayMessage({ message: error, type: 'error' }))
                return navigate(RouteConstants.clients)
            }
            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    const deleteClient = async () => {
        try {
            await ClientManager.getManager().deleteClientAsync(id)
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        navigate(RouteConstants.clients)
        dispatch(displayMessage({ message: 'Klient smazán', type: 'success' }))
        dispatch(setSaveState(true))
    }

    const saveEditClient = async () => {
        setLoadingData(true)
        const clientManager = ClientManager.getManager()
        try {
            const clientRes = await clientManager.updateClientAsync(id, client)
            if (clientRes) setClient(clientRes)
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingData(false)
        dispatch(displayMessage({ message: 'Klient uložen', type: 'success' }))
    }

    if (loadingData) return <ScreenLoadingPlaceholder />

    return (
        <CustomContainer>
            <Grid container rowSpacing={3} columnSpacing={3} mb={5}>
                <Grid item xs={12}>
                    <ClientBasicInformation client={client} onChange={setClient} />
                </Grid>

                <Grid item xs={12}>
                    <EventTable events={client.events} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <EmailSwitches
                        emailSwitches={client.emailMessageSwitches}
                        onChange={(emailSwitches) => setClient({ ...client, emailMessageSwitches: emailSwitches })}
                    />
                </Grid>
            </Grid>
            <BottomBar onDelete={deleteClient} onSave={saveEditClient} defaultRoute={RouteConstants.clients} disabled={loadingData} deletedEntityName={client.name} />
        </CustomContainer>
    )
}

export default ClientDetail
